import { ApiResponse } from "@/app/types/shared_types";
import { Staff, StaffTable } from "@/app/types/staff";
import { apiSlice } from "@/features/api/ApiSlice";

export enum Status {
  Active = "active",
  Inactive = "inactive",
  Pending = "pending",
}
export const Staffs = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStaffById: builder.query<Staff, number>({
      query: (staff_id) => ({
        url: `/staff/${staff_id}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: (result, error, arg) => [{ type: "Staffs", id: arg }],
    }),
    createStaff: builder.mutation<ApiResponse, Staff>({
      query: (staffdata) => ({
        url: "/staff",
        method: "POST",
        body: staffdata,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Staffs"],
    }),
    getStaffs: builder.query<StaffTable, { query: string }>({
      query: ({ query }) => ({
        url: `/staff${query ? "?" + query : ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Staffs"],
    }),
    getStaffList: builder.query<
      { value: string; label: string; contact_number: string, designation?: string, email?: string }[],
      { query: string }
    >({
      query: ({ query }) => ({
        url: `/staff${query ? "?" + query : ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Staffs", "Counters"],
      transformResponse: (resp: {
        data: {
          id: string;
          first_name: string;
          last_name: string;
          status: Status;
          counter_id: number | null;
          phone_num: string;
          designation?: string;
          email?: string;
        }[];
      }) => {
        return resp.data
          .filter((staff) => staff.status !== Status.Inactive)
          .map(
            (staff: {
              id: string;
              first_name: string;
              last_name: string;
              status: Status;
              counter_id: number | null;
              phone_num: string;
              designation?: string;
              email?: string;
            }) => ({
              value: staff.id,
              label: `${staff.first_name} ${staff.last_name}`,
              contact_number: staff.phone_num,
              designation: staff.designation,
              email: staff.email,
            })
          );
      },
    }),
    getStaffListWithAssignCounter: builder.query<
      {
        value: string;
        label: string;
        AssignedCounter: boolean;
        status: Status; // Ensures the status is included
      }[],
      string
    >({
      query: (query) => ({
        url: `/staff?${query}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Staffs"],
      transformResponse: (resp: {
        data: {
          id: string;
          first_name: string;
          last_name: string;
          status: Status;
          counter_id: number | null;
        }[];
      }) => {
        return resp.data.map((staff) => ({
          value: staff.id,
          label: `${staff.first_name} ${staff.last_name}`,
          AssignedCounter: typeof staff.counter_id === "number",
          status: staff.status, // Adds the `status` field to the mapped response
        }));
      },
    }),

    updateStaff: builder.mutation<ApiResponse, Staff>({
      query: (staffdata) => ({
        url: `/staff/${staffdata.id}`,
        method: "PUT",
        body: staffdata,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Staffs"],
    }),
    deleteStaff: builder.mutation<ApiResponse, number>({
      query: (staffId) => ({
        url: `/staff/${staffId}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Staffs"],
    }),
    getStaffCounterDetails: builder.query<
      {
        id: string;
        counter_id: number;
        service_name: string;
        service_id: number;
        department_id: number;
        department_name: string;
      },
      {
        id: string;
        event_id?: number;
      }
    >({
      query: ({ id, event_id }) => ({
        url: `/staff/${id}/counter${event_id ? `?event_id=${event_id}` : ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Counters", "Service"],
    }),
  }),
});

export const {
  useGetStaffsQuery,
  useGetStaffByIdQuery,
  useGetStaffListQuery,
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useDeleteStaffMutation,
  useGetStaffListWithAssignCounterQuery,
  useGetStaffCounterDetailsQuery
} = Staffs;
